import React, { useEffect, useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import { webFilterService, WfDetailException, WfDetailExceptionInput, WfBulkUploadSkip } from '../../../services/webFilter.service';
import { useAuth } from '../../../contexts/AuthContext';
import BulkUploadBar from '../common/BulkUploadBar';
import { EXCEPTION_SAMPLE_CSV } from '../../../lib/exceptionSampleCsv';
import { WF_EXCEPT_DETAILS, isAllowedActor } from '../../../lib/exceptionOptions';
import { exceptionActorService } from '../../../services/exception-actor.service';
import { securityExceptionService, ExceptionCompany } from '../../../services/security-exception.service';
import { ActorRejection, toActorRejection } from '../../../lib/actorRejection';
import {
  SectionHeader, SearchBar, FormCard, Fieldset, Field, TableShell,
  SortableTh, Th, Row, Td, RowActions, LoadingState, EmptyState, Pagination,
  Input, Select, Textarea, SearchableSelect,
} from '../../ui';

const PAGE_LIMIT = 50;

const COMPANY_GROUPS = ['세아홀딩스 계열', '세아제강지주 계열'];

const EXCEPT_GROUPS = ['웹사이트 예외', '웹사이트 차단', '기타'];

const EMPTY_FORM: WfDetailExceptionInput = {
  requestNo: undefined,
  exceptGroup: '',
  exceptDetail: '',
  companyGroup: '',
  reqCompany: '',
  reqDepartment: '',
  reqName: '',
  targetCompany: '',
  permittedIp: '',
  permittedPc: '',
  url: '',
  reason: '',
  period: '',
  approval: '',
  actor: '',
  applyDateDisplay: '',
  endDate: '',
  applyMethod: '',
  remark: '',
};

/**
 * IPT-599 — 신규 등록 시 미입력 항목을 전송 본문에서 제외한다.
 * EMPTY_FORM은 모든 필드를 ''로 초기화하는데, 백엔드 선택 필드는 빈 문자열을 값으로 취급한다.
 * 특히 period는 입력란이 없는데도 ''로 전송되어(서버가 적용일에서 자동 도출) 등록이 거부됐다.
 * 수정은 이 정규화를 쓰지 않는다 — 빈 값을 보내야 기존 값을 지울 수 있다.
 */
const omitBlank = (input: WfDetailExceptionInput): WfDetailExceptionInput =>
  Object.fromEntries(
    Object.entries(input).filter(([, v]) => v !== '' && v !== undefined),
  ) as WfDetailExceptionInput;

const WfExceptionSection: React.FC = () => {
  const { user } = useAuth();
  const isAdminOrEditor = user?.role === 'admin' || user?.role === 'editor';

  const [rows, setRows] = useState<WfDetailException[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  // 업로드에서 제외된 행 — 토스트는 사라지므로 사유를 화면에 남긴다
  const [uploadSkipped, setUploadSkipped] = useState<WfBulkUploadSkip[]>([]);
  // IPT-613 — 처리자 값 때문에 파일 전체가 거부된 경우 (저장 0건)
  const [actorReject, setActorReject] = useState<ActorRejection | null>(null);

  const [filterGroup, setFilterGroup] = useState('');
  const [filterCompany, setFilterCompany] = useState('');
  const [filterSearch, setFilterSearch] = useState('');
  const [sortKey, setSortKey] = useState('');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const [showForm, setShowForm] = useState(false);
  const [editingRow, setEditingRow] = useState<WfDetailException | null>(null);
  const [form, setForm] = useState<WfDetailExceptionInput>(EMPTY_FORM);
  // IPT-613 — 기한 '영구' 체크 상태 (기한 없음 = 영구)
  const [isPermanent, setIsPermanent] = useState(false);
  // 처리자 후보는 DB 마스터에서 받아온다 (관리자 화면에서 추가·삭제)
  const [actorOptions, setActorOptions] = useState<string[]>([]);
  // IPT-624 — 대상 계열사를 자유 입력이 아닌 드롭다운으로 (DLP 예외처리와 동일하게)
  const [companies, setCompanies] = useState<ExceptionCompany[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_LIMIT));

  const load = useCallback(async (pg: number) => {
    setIsLoading(true);
    try {
      const result = await webFilterService.getDetailExceptions({
        companyGroup: filterGroup || undefined,
        reqCompany: filterCompany || undefined,
        search: filterSearch || undefined,
        page: pg,
        limit: PAGE_LIMIT,
        sortKey: sortKey || undefined,
        sortDir,
      });
      setRows(result.items);
      setTotal(result.total);
    } catch {
      toast.error('목록을 불러오는 데 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  }, [filterGroup, filterCompany, filterSearch, sortKey, sortDir]);

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
    setPage(1);
  };

  useEffect(() => {
    exceptionActorService
      .getAll()
      .then((list) => setActorOptions(list.map((a) => a.name)))
      .catch(() => setActorOptions([]));
    securityExceptionService.getCompanies().then(setCompanies).catch(() => setCompanies([]));
  }, []);

  useEffect(() => {
    setPage(1);
  }, [filterGroup, filterCompany, filterSearch]);

  useEffect(() => {
    load(page);
  }, [page, load]);

  const openCreate = () => {
    setEditingRow(null);
    setForm(EMPTY_FORM);
    setIsPermanent(false);
    setShowForm(true);
  };

  const rowToForm = (row: WfDetailException): WfDetailExceptionInput => ({
    requestNo: row.requestNo ?? undefined,
    exceptGroup: row.exceptGroup ?? '',
    exceptDetail: row.exceptDetail ?? '',
    companyGroup: row.companyGroup ?? '',
    reqCompany: row.reqCompany ?? '',
    reqDepartment: row.reqDepartment ?? '',
    reqName: row.reqName ?? '',
    targetCompany: row.targetCompany ?? '',
    permittedIp: row.permittedIp ?? '',
    permittedPc: row.permittedPc ?? '',
    url: row.url ?? '',
    reason: row.reason ?? '',
    period: row.period ?? '',
    approval: row.approval ?? '',
    actor: row.actor ?? '',
    applyDateDisplay: row.applyDateDisplay ?? '',
    endDate: row.endDate ? String(row.endDate).slice(0, 10) : '',
    applyMethod: row.applyMethod ?? '',
    remark: row.remark ?? '',
  });

  const openEdit = (row: WfDetailException) => {
    setEditingRow(row);
    setForm(rowToForm(row));
    setIsPermanent(!row.endDate);
    setShowForm(true);
  };

  /**
   * 복제 — editingRow를 null로 두어 신규 등록으로 처리한다.
   * requestNo는 DB에서 unique 제약이고 폼에 노출되지 않는 값이라, 그대로 들고 가면
   * 저장(create) 단계에서 원본과 충돌한다 — 반드시 비워 새로 받는다.
   */
  const openDuplicate = (row: WfDetailException) => {
    setEditingRow(null);
    setForm({ ...rowToForm(row), requestNo: undefined });
    setIsPermanent(!row.endDate);
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('삭제하시겠습니까?')) return;
    try {
      await webFilterService.deleteDetailException(id);
      toast.success('삭제되었습니다.');
      load(page);
    } catch {
      toast.error('삭제에 실패했습니다.');
    }
  };

  const handleSave = async () => {
    // IPT-613 — 목록 밖 처리자는 저장하지 않는다. 그대로 저장하면 값이 지워지므로,
    // 어떤 값이 문제인지 폼에 남겨두고(actorInvalid 경고) 다시 고르게 한다.
    if (!isAllowedActor(form.actor, actorOptions)) return;
    // IPT-624 — 결재문서번호·적용그룹(applyMethod)은 필수 입력
    if (!form.approval?.trim() || !form.applyMethod?.trim()) {
      alert('결재문서번호와 적용그룹은 필수 입력 항목입니다.');
      return;
    }

    setIsSaving(true);
    try {
      const dto: WfDetailExceptionInput = {
        ...form,
        requestNo: form.requestNo ? Number(form.requestNo) : undefined,
        // 영구면 기한을 비워 보낸다 — 수정 시 서버가 null로 덮어쓰고, 등록 시 omitBlank가 제거한다
        endDate: isPermanent ? '' : form.endDate,
      };
      if (editingRow) {
        await webFilterService.updateDetailException(editingRow.id, dto);
        toast.success('수정되었습니다.');
      } else {
        await webFilterService.createDetailException(omitBlank(dto));
        toast.success('등록되었습니다.');
      }
      setShowForm(false);
      load(page);
    } catch {
      toast.error('저장에 실패했습니다.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpload = async (file: File) => {
    setActorReject(null);
    try {
      const result = await webFilterService.uploadDetailExceptionsExcel(file);
      setUploadSkipped(result.skipped ?? []);
      toast.success(`업로드 완료. ${result.inserted}건 등록.`);
      load(1);
    } catch (e) {
      const rejection = toActorRejection(e);
      if (rejection) {
        // 처리자 값 오류는 서버가 저장 전에 파일 전체를 거부한다 — 어떤 행이 걸렸는지 화면에 남긴다
        setUploadSkipped([]);
        setActorReject(rejection);
      } else {
        toast.error('업로드에 실패했습니다.');
      }
      throw new Error('upload failed');
    }
  };

  const v = (val?: string | null) => val || '-';
  const set = (patch: Partial<WfDetailExceptionInput>) => setForm((f) => ({ ...f, ...patch }));

  const columns: { label: string; key: string }[] = [
    { label: '예외항목(그룹)', key: 'exceptGroup' },
    { label: '예외항목(상세)', key: 'exceptDetail' },
    { label: '계열사그룹', key: 'companyGroup' },
    { label: '신청계열사', key: 'reqCompany' },
    { label: '신청부서', key: 'reqDepartment' },
    { label: '신청자', key: 'reqName' },
    { label: '대상계열사', key: 'targetCompany' },
    { label: '허용IP', key: 'permittedIp' },
    { label: '허용PC', key: 'permittedPc' },
    { label: 'URL', key: 'url' },
    { label: '사유', key: 'reason' },
    { label: '기간', key: 'period' },
    { label: '결재문서번호', key: 'approval' },
    { label: '처리자', key: 'actor' },
    { label: '적용일', key: 'applyDateDisplay' },
    { label: '기한', key: 'endDate' },
    { label: '적용그룹', key: 'applyMethod' },
    { label: '비고', key: 'remark' },
  ];

  return (
    <div>
      <SectionHeader
        title="유해차단 예외처리 관리"
        total={total}
        onAdd={isAdminOrEditor ? openCreate : undefined}
        addLabel="+ 예외처리 추가"
        description="유해사이트 차단 솔루션(WebKeeper) 예외처리 신청 및 관리"
      />

      {/* 벌크 업로드 — 상단 */}
      {isAdminOrEditor && (
        <div className="mb-5">
          <BulkUploadBar sample={EXCEPTION_SAMPLE_CSV.wf} onUpload={handleUpload} />
        </div>
      )}

      {/* 처리자 값 오류로 파일 전체가 거부된 경우 (IPT-613) */}
      {actorReject && (
        <div
          role="alert"
          className="mb-5 rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive"
          style={{ wordBreak: 'keep-all', overflowWrap: 'break-word' }}
        >
          <div className="flex items-start justify-between gap-3">
            <p className="font-semibold">{actorReject.message}</p>
            <button type="button" onClick={() => setActorReject(null)} className="shrink-0 text-sm underline">
              닫기
            </button>
          </div>
          <ul className="mt-2 space-y-1">
            {actorReject.invalidActors.map((r) => (
              <li key={`${r.row}-${r.value}`}>
                {r.row}행 — ‘{r.value}’
              </li>
            ))}
          </ul>
          {actorReject.allowedActors.length > 0 && (
            <p className="mt-2 text-xs opacity-80">
              사용 가능: {actorReject.allowedActors.join(' · ')}
            </p>
          )}
        </div>
      )}

      {/* 업로드에서 제외된 행 — 토스트로는 확인이 어려워 사유를 남긴다 */}
      {uploadSkipped.length > 0 && (
        <div
          role="alert"
          className="mb-5 rounded-lg border border-amber-300 bg-amber-50 p-4"
          style={{ wordBreak: 'keep-all', overflowWrap: 'break-word' }}
        >
          <div className="flex items-start justify-between gap-3">
            <p className="font-semibold text-amber-900">
              {uploadSkipped.length}건이 등록되지 않았습니다
            </p>
            <button
              type="button"
              onClick={() => setUploadSkipped([])}
              className="shrink-0 text-sm text-amber-700 underline"
            >
              닫기
            </button>
          </div>
          <ul className="mt-2 space-y-1 text-sm text-amber-900">
            {uploadSkipped.map((s, i) => (
              <li key={`${s.requestNo ?? 'no'}-${i}`}>
                {[
                  s.requestNo != null ? `요청번호 ${s.requestNo}` : null,
                  s.reqCompany,
                  s.url,
                ]
                  .filter(Boolean)
                  .join(' · ')}
                {' — '}
                {s.reason}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* 검색 바 — 하단 */}
      <SearchBar>
        <Select value={filterGroup} onChange={(e) => setFilterGroup(e.target.value)} className="w-auto min-w-[150px]">
          <option value="">전체 계열사 그룹</option>
          {COMPANY_GROUPS.map((g) => <option key={g} value={g}>{g}</option>)}
        </Select>
        <Input placeholder="신청계열사" value={filterCompany} onChange={(e) => setFilterCompany(e.target.value)} className="w-[140px]" />
        <Input placeholder="이름/계열사/URL 검색" value={filterSearch} onChange={(e) => setFilterSearch(e.target.value)} className="w-[180px]" />
      </SearchBar>

      {/* 등록/수정 인라인 카드 */}
      {showForm && (
        <FormCard
          title={`유해차단 예외처리 ${editingRow ? '수정' : '등록'}`}
          onClose={() => setShowForm(false)}
          onSave={handleSave}
          isSaving={isSaving}
          saveLabel={editingRow ? '수정 저장' : '추가'}
        >
          {/* 목록 밖 처리자 — 어떤 값이 걸렸는지 보여주고 저장을 막는다 */}
          {!isAllowedActor(form.actor, actorOptions) && (
            <div
              role="alert"
              className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900"
              style={{ wordBreak: 'keep-all' }}
            >
              저장된 처리자 <strong>‘{form.actor}’</strong>은(는) 선택할 수 있는 값이 아닙니다.
              아래에서 처리자를 다시 고른 뒤 저장해 주세요.
            </div>
          )}

          <Fieldset legend="예외 항목" accent>
            <Field label="예외항목(그룹)">
              <Select value={form.exceptGroup || ''} onChange={(e) => set({ exceptGroup: e.target.value })}>
                <option value="">선택</option>
                {EXCEPT_GROUPS.map((g) => <option key={g} value={g}>{g}</option>)}
              </Select>
            </Field>
            <Field label="예외항목(상세)" htmlFor="wf-except-detail">
              {/*
                IPT-613 — 후보가 48개라 select로는 찾기 어렵다. 타이핑하면 일치 항목만
                좁혀 보여주고, 목록 밖 값도 그대로 값이 된다 — 운영 DB의 '임시 웹페이지'처럼
                후보에 없는 기존 값이 수정 시 지워지지 않아야 하기 때문이다.
              */}
              <SearchableSelect
                id="wf-except-detail"
                value={form.exceptDetail || ''}
                onChange={(v) => set({ exceptDetail: v })}
                options={WF_EXCEPT_DETAILS}
                placeholder="검색하거나 선택 (예: 웹메일)"
              />
            </Field>
            <Field label="계열사 그룹">
              <Select value={form.companyGroup || ''} onChange={(e) => set({ companyGroup: e.target.value })}>
                <option value="">선택</option>
                {COMPANY_GROUPS.map((g) => <option key={g} value={g}>{g}</option>)}
              </Select>
            </Field>
            <Field label="대상 계열사" htmlFor="wf-target-company">
              {/* IPT-624 — DLP 예외처리와 동일하게 자유 입력이 아닌 드롭다운으로 */}
              <Select id="wf-target-company" value={form.targetCompany || ''} onChange={(e) => set({ targetCompany: e.target.value })}>
                <option value="">선택하세요</option>
                {companies.map((c) => <option key={c.orgCode} value={c.companyName}>{c.companyName}</option>)}
              </Select>
            </Field>
          </Fieldset>

          <Fieldset legend="신청자 정보">
            <Field label="신청 계열사">
              <Input value={form.reqCompany || ''} onChange={(e) => set({ reqCompany: e.target.value })} placeholder="신청 계열사" />
            </Field>
            <Field label="신청 부서">
              <Input value={form.reqDepartment || ''} onChange={(e) => set({ reqDepartment: e.target.value })} placeholder="신청 부서" />
            </Field>
            <Field label="신청자">
              <Input value={form.reqName || ''} onChange={(e) => set({ reqName: e.target.value })} placeholder="신청자 이름" />
            </Field>
            <Field label="처리자" htmlFor="wf-actor">
              <Select id="wf-actor" value={form.actor || ''} onChange={(e) => set({ actor: e.target.value })}>
                <option value="">선택</option>
                {actorOptions.map((a) => (
                  <option key={a} value={a}>{a}</option>
                ))}
              </Select>
            </Field>
            <Field label="결재문서번호" htmlFor="wf-approval">
              <Input id="wf-approval" value={form.approval || ''} onChange={(e) => set({ approval: e.target.value })} placeholder="결재문서번호 입력" />
            </Field>
          </Fieldset>

          <Fieldset legend="접근 대상">
            <Field label="허용 IP">
              <Input value={form.permittedIp || ''} onChange={(e) => set({ permittedIp: e.target.value })} placeholder="192.168.1.1" className="font-mono" />
            </Field>
            <Field label="허용 PC">
              <Input value={form.permittedPc || ''} onChange={(e) => set({ permittedPc: e.target.value })} placeholder="PC 이름" />
            </Field>
            <Field label="URL">
              <Input value={form.url || ''} onChange={(e) => set({ url: e.target.value })} placeholder="example.com" />
            </Field>
          </Fieldset>

          <Fieldset legend="처리 기간" minColWidth={180}>
            <Field label="적용일">
              <Input value={form.applyDateDisplay || ''} onChange={(e) => set({ applyDateDisplay: e.target.value })} placeholder="2026-04-01" />
            </Field>
            <Field label="기한" htmlFor="wf-end-date">
              {/*
                IPT-613 — NAC과 같은 '영구' 체크박스. 저장 결과는 종전과 같다(기한 없음 = 영구).
                수정 시에는 빈 문자열을 보내야 서버가 endDate를 null로 덮어쓴다
                (web-filter.service.ts:1122의 endDate !== undefined 가드).
              */}
              <div className="flex items-center gap-2">
                <Input
                  id="wf-end-date"
                  type="date"
                  value={form.endDate || ''}
                  onChange={(e) => set({ endDate: e.target.value })}
                  disabled={isPermanent}
                  className={isPermanent ? 'flex-1 opacity-50' : 'flex-1'}
                />
                <label htmlFor="wf-permanent" className="flex cursor-pointer items-center gap-1 whitespace-nowrap text-xs">
                  <input
                    id="wf-permanent"
                    type="checkbox"
                    checked={isPermanent}
                    onChange={(e) => {
                      setIsPermanent(e.target.checked);
                      if (e.target.checked) set({ endDate: '' });
                    }}
                  />
                  영구
                </label>
              </div>
            </Field>
            <Field label="적용그룹" htmlFor="wf-apply-method">
              {/* IPT-624 — 라벨을 '적용방법'에서 '적용그룹'으로 변경. 내부 필드명(applyMethod)·DB컬럼·엑셀헤더는 하위호환을 위해 유지 */}
              <Input id="wf-apply-method" value={form.applyMethod || ''} onChange={(e) => set({ applyMethod: e.target.value })} placeholder="정책명 입력" />
            </Field>
          </Fieldset>

          <Fieldset legend="상세 내용" columns="1fr 1fr">
            <Field label="사유">
              <Textarea value={form.reason || ''} onChange={(e) => set({ reason: e.target.value })} rows={3} placeholder="사유 입력" />
            </Field>
            <Field label="비고">
              <Textarea value={form.remark || ''} onChange={(e) => set({ remark: e.target.value })} rows={3} placeholder="비고 입력" />
            </Field>
          </Fieldset>
        </FormCard>
      )}

      {/* 목록 테이블 */}
      {isLoading ? (
        <LoadingState />
      ) : rows.length === 0 ? (
        <EmptyState text="데이터가 없습니다." />
      ) : (
        <TableShell
          headers={
            <>
              {columns.map((c) => (
                <SortableTh key={c.key} label={c.label} sortKey={c.key} activeKey={sortKey} dir={sortDir} onSort={handleSort} />
              ))}
              {isAdminOrEditor && <Th>관리</Th>}
            </>
          }
          footer={<Pagination page={page} totalPages={totalPages} onPage={(p) => setPage(p)} />}
        >
          {rows.map((row, idx) => (
            <Row key={row.id} idx={idx}>
              <Td>{v(row.exceptGroup)}</Td>
              <Td>{v(row.exceptDetail)}</Td>
              <Td className="whitespace-nowrap">{v(row.companyGroup)}</Td>
              <Td>{v(row.reqCompany)}</Td>
              <Td>{v(row.reqDepartment)}</Td>
              <Td>{v(row.reqName)}</Td>
              <Td>{v(row.targetCompany)}</Td>
              <Td className="font-mono">{v(row.permittedIp)}</Td>
              <Td>{v(row.permittedPc)}</Td>
              <Td className="max-w-[120px] overflow-hidden text-ellipsis whitespace-nowrap">{v(row.url)}</Td>
              <Td className="max-w-[150px] overflow-hidden text-ellipsis whitespace-nowrap">{v(row.reason)}</Td>
              <Td className="whitespace-nowrap">{v(row.period)}</Td>
              <Td>{v(row.approval)}</Td>
              <Td>{v(row.actor)}</Td>
              <Td className="whitespace-nowrap">{v(row.applyDateDisplay)}</Td>
              <Td className="whitespace-nowrap">{row.endDate ? String(row.endDate).slice(0, 10) : '영구'}</Td>
              <Td>{v(row.applyMethod)}</Td>
              <Td className="max-w-[120px] overflow-hidden text-ellipsis whitespace-nowrap">{v(row.remark)}</Td>
              {isAdminOrEditor && (
                <Td className="whitespace-nowrap">
                  <RowActions
                    onEdit={() => openEdit(row)}
                    onDelete={() => handleDelete(row.id)}
                    onDuplicate={() => openDuplicate(row)}
                  />
                </Td>
              )}
            </Row>
          ))}
        </TableShell>
      )}
    </div>
  );
};

export default WfExceptionSection;
